// ── Mock "OneSDK Studio" — theme the hosted IDV flow's review screen ─────────
//
// Studio settings persist on the task itself (`parameters.oneSdkStudio`) so
// they survive closing/reopening the drawer. "Generate & attach HTML" writes
// the rendered markup out as a real config file on the node
// (`onesdk_review.html`), which then shows up in the Related config files
// section at the bottom of the drawer — this is the "studio produces a file
// you can also hand-edit" workflow.
import { useOpsCenterStore } from '../../store'
import type { ConfigFile, StepTask } from '../../types'
import { Field, TextInput, Toggle, getParams, setParam } from './fields'

export interface OneSdkStudioProps {
  task: StepTask
  onChange: (updater: (t: StepTask) => StepTask) => void
}

interface OneSdkStudioSettings {
  themeColor: string
  logoUrl: string
  buttonColor: string
  buttonLabel: string
  showReviewScreen: boolean
}

const DEFAULT_SETTINGS: OneSdkStudioSettings = {
  themeColor: '#6366f1',
  logoUrl: '',
  buttonColor: '#6366f1',
  buttonLabel: 'Continue',
  showReviewScreen: true,
}

const GENERATED_FILE_NAME = 'onesdk_review.html'

function buildHtml(s: OneSdkStudioSettings): string {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, sans-serif; background: #f4f5f7;
             display: flex; align-items: center; justify-content: center; min-height: 100vh; }
      .card { background: #fff; border-radius: 12px; padding: 24px; width: 280px;
              box-shadow: 0 4px 16px rgba(0,0,0,0.08); text-align: center; }
      .logo { height: 32px; margin-bottom: 12px; }
      h1 { font-size: 16px; color: ${s.themeColor}; margin: 0 0 8px; }
      p { font-size: 12px; color: #667085; margin: 0 0 16px; }
      button { background: ${s.buttonColor}; color: #fff; border: none; border-radius: 8px;
               padding: 10px 20px; font-size: 13px; font-weight: 600; cursor: pointer; width: 100%; }
    </style>
  </head>
  <body>
    <div class="card">
      ${s.logoUrl ? `<img class="logo" src="${s.logoUrl}" alt="logo" />` : ''}
      <h1>Verify your identity</h1>
      <p>${s.showReviewScreen ? 'Review your details before submitting.' : 'Complete verification to continue.'}</p>
      <button type="button">${s.buttonLabel}</button>
    </div>
  </body>
</html>`
}

export function OneSdkStudio({ task, onChange }: OneSdkStudioProps) {
  const wf = useOpsCenterStore((s) => s.workflows.find((w) => w.id === s.selectedWorkflowId))
  const updateWorkflow = useOpsCenterStore((s) => s.updateWorkflow)

  const params = getParams(task)
  const settings: OneSdkStudioSettings = {
    ...DEFAULT_SETTINGS,
    ...(params.oneSdkStudio as Partial<OneSdkStudioSettings> | undefined),
  }
  const html = buildHtml(settings)

  function patch(p: Partial<OneSdkStudioSettings>) {
    setParam(onChange, 'oneSdkStudio', { ...settings, ...p })
  }

  function handleGenerate() {
    if (!wf) return
    const file: ConfigFile = { name: GENERATED_FILE_NAME, content: html }
    const existing = (wf.configFiles?.[task.id] ?? []).filter((f) => f.name !== GENERATED_FILE_NAME)
    updateWorkflow(wf.id, {
      configFiles: { ...(wf.configFiles ?? {}), [task.id]: [...existing, file] },
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[10px] uppercase tracking-wide text-[#5b6b85]">OneSDK Studio</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Theme color">
          <input
            type="color"
            value={settings.themeColor}
            onChange={(e) => patch({ themeColor: e.target.value })}
            className="w-full h-8 bg-[#0b1220] border border-[#1e2c44] rounded-md cursor-pointer"
          />
        </Field>
        <Field label="Button color">
          <input
            type="color"
            value={settings.buttonColor}
            onChange={(e) => patch({ buttonColor: e.target.value })}
            className="w-full h-8 bg-[#0b1220] border border-[#1e2c44] rounded-md cursor-pointer"
          />
        </Field>
      </div>

      <Field label="Logo URL">
        <TextInput
          value={settings.logoUrl}
          onChange={(e) => patch({ logoUrl: e.target.value })}
          placeholder="https://…/logo.png"
        />
      </Field>

      <Field label="Button label">
        <TextInput value={settings.buttonLabel} onChange={(e) => patch({ buttonLabel: e.target.value })} />
      </Field>

      <Toggle
        label="Show review screen"
        checked={settings.showReviewScreen}
        onChange={(v) => patch({ showReviewScreen: v })}
      />

      <div>
        <p className="text-[10px] uppercase tracking-wide text-[#5b6b85] mb-1.5">Live preview</p>
        <iframe
          srcDoc={html}
          title="OneSDK preview"
          sandbox=""
          className="w-full h-40 rounded-md border border-[#1e2c44] bg-white"
        />
      </div>

      <button
        onClick={handleGenerate}
        disabled={!wf}
        className="self-start px-2.5 py-1.5 rounded-md text-xs font-medium bg-[#6366f1] text-white hover:bg-[#818cf8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Generate &amp; attach HTML
      </button>
    </div>
  )
}
